import { SALESFORCE_SERVICE_ID } from "components/ConfigureStep/SalesforceTriggers/salesforceTriggerModel";

/**
 * The four Salesforce steps of the new sync (v2): Update Salesforce Field,
 * Create Task in Salesforce, Add to Campaign in Salesforce and Remove from
 * Campaign in Salesforce, worked out from a step's stored settings. Pure
 * functions with no React in them, so the words and the rules can be tested on
 * their own. Copy rules: plain words, no em dashes, "within about 10 minutes"
 * and never "real time". The wording is from the in-app copy file (flow builder
 * steps); each key is named in a comment where it is used.
 *
 * The settings are the ones the service definition names and the server reads
 * (packages/service-definitions/operations/salesforce-v2-steps.operations.json,
 * packages/api/src/services/salesforce/v2/steps): nothing else is stored. A
 * Campaign is stored by its Salesforce id only and a member status by its name,
 * so a card that wants to say which Campaign looks the name up (see
 * SalesforceStepCardText).
 */

export const UPDATE_FIELD_OPERATION = "/update-field";
export const CREATE_TASK_OPERATION = "/create-task";
export const ADD_TO_CAMPAIGN_OPERATION = "/add-to-campaign";
export const REMOVE_FROM_CAMPAIGN_OPERATION = "/remove-from-campaign";

export const SALESFORCE_V2_STEP_OPERATIONS: readonly string[] = [
  UPDATE_FIELD_OPERATION,
  CREATE_TASK_OPERATION,
  ADD_TO_CAMPAIGN_OPERATION,
  REMOVE_FROM_CAMPAIGN_OPERATION,
];

/** The lists the setup tabs read: the dynamic operations of the same definition. */
export const STEP_LIST_OPERATIONS = {
  objects: "/update-field/objects",
  fields: "/update-field/fields",
  campaigns: "/campaigns",
  statuses: "/campaign-member-statuses",
  owners: "/task-owners",
} as const;

type StepLike = { serviceId?: string; operationKey?: string };

const is = (item: StepLike, operation: string) =>
  item.serviceId === SALESFORCE_SERVICE_ID && item.operationKey === operation;

/** Whether a step or a tool is one of the four new Salesforce steps. */
export const isSalesforceV2Step = (item: StepLike): boolean =>
  item.serviceId === SALESFORCE_SERVICE_ID &&
  typeof item.operationKey === "string" &&
  SALESFORCE_V2_STEP_OPERATIONS.includes(item.operationKey);

export const isUpdateFieldStep = (item: StepLike) =>
  is(item, UPDATE_FIELD_OPERATION);
export const isCreateTaskStep = (item: StepLike) =>
  is(item, CREATE_TASK_OPERATION);
export const isAddToCampaignStep = (item: StepLike) =>
  is(item, ADD_TO_CAMPAIGN_OPERATION);
export const isRemoveFromCampaignStep = (item: StepLike) =>
  is(item, REMOVE_FROM_CAMPAIGN_OPERATION);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** A value the form holds as text: text as it is, a number or a checkbox as its words. */
const asText = (value: unknown): string =>
  typeof value === "string"
    ? value
    : typeof value === "number" || typeof value === "boolean"
    ? String(value)
    : "";

/** A long value cut to a length a card or a sentence can hold. */
export const shortened = (text: string, max = 40): string =>
  text.length > max ? `${text.slice(0, max - 3).trimEnd()}...` : text;

const MAX_NAMES = 3;

/** "A", "A and B", "A, B and C", and past three "A, B and 2 more". */
export const andList = (items: string[]): string => {
  if (items.length <= 1) return items.join("");
  if (items.length <= MAX_NAMES) {
    return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
  }
  const shown = items.slice(0, MAX_NAMES - 1);
  return `${shown.join(", ")} and ${items.length - shown.length} more`;
};

// ---------------------------------------------------------------------------
// The lists

/** An item of a list: the server's { id, title }. */
export type StepPick = { id: string; title: string };

/** The items of a list answer, leaving out anything that is not an { id, title }. */
export const readPicks = (answer: unknown): StepPick[] =>
  Array.isArray(answer)
    ? answer.flatMap((item) =>
        isRecord(item) &&
        typeof item.id === "string" &&
        item.id !== "" &&
        typeof item.title === "string"
          ? [{ id: item.id, title: item.title }]
          : []
      )
    : [];

export type SelectOption = { value: string; label: string };
export type SelectGroup = { label: string; options: SelectOption[] };

/**
 * The server marks a Campaign that is not active by ending its name in
 * " (inactive)" (stepPickers.ts, campaignsPicker); it lists the active ones
 * first. The marker is the only sign, so it is read here and taken off the name.
 */
const INACTIVE_SUFFIX = " (inactive)";
export const isInactiveCampaign = (title: string): boolean =>
  title.endsWith(INACTIVE_SUFFIX);
export const campaignName = (title: string): string =>
  isInactiveCampaign(title) ? title.slice(0, -INACTIVE_SUFFIX.length) : title;

/**
 * The Campaigns as picker groups: active ones first, then the others
 * (flow.campaign.group.active / .other, as the trigger's picker has them). An
 * empty group is left out.
 */
export const campaignOptionGroups = (picks: StepPick[]): SelectGroup[] => {
  const option = (pick: StepPick): SelectOption => ({
    value: pick.id,
    label: campaignName(pick.title),
  });
  const active = picks.filter((pick) => !isInactiveCampaign(pick.title));
  const other = picks.filter((pick) => isInactiveCampaign(pick.title));
  const groups: SelectGroup[] = [];
  if (active.length > 0) {
    groups.push({ label: "Active campaigns", options: active.map(option) });
  }
  if (other.length > 0) {
    groups.push({ label: "Other campaigns", options: other.map(option) });
  }
  return groups;
};

/** The name of the Campaign with this id in a list, when the list has it. */
export const campaignNameFor = (
  picks: StepPick[] | undefined,
  id: string
): string | undefined => {
  const found = picks?.find((pick) => pick.id === id);
  return found ? campaignName(found.title) : undefined;
};

/**
 * The most Campaigns or users the server lists (PICKER_LIMIT in stepPickers.ts).
 * A list this long may have been cut, so a choice that is not in it is not
 * necessarily gone.
 */
export const PICKER_LIST_LIMIT = 2000;

/**
 * Whether a list answer can be trusted to say a choice is gone: it was read, it
 * has something in it, and it was not cut at the limit.
 */
export const listIsComplete = (picks: StepPick[] | undefined): boolean =>
  Boolean(picks && picks.length > 0 && picks.length < PICKER_LIST_LIMIT);

/**
 * The member statuses as picker options. A status is stored by its name (the
 * id of the list's item); the list marks the Campaign's default one by ending
 * its title in " (default)", which the picker shows.
 */
export const statusOptions = (picks: StepPick[]): SelectOption[] =>
  picks.map((pick) => ({ value: pick.id, label: pick.title }));

// ---------------------------------------------------------------------------
// Errors of the lists

export type StepListKind =
  | "objects"
  | "fields"
  | "campaigns"
  | "statuses"
  | "owners";

export type StepListError = {
  status?: number;
  /** The server's sentence, without the engine's "Error while contacting" lead-in */
  serverText?: string;
};

/** flow.pick.error, flow.campaign.error.list */
const FALLBACK_TEXT: Record<StepListKind, string> = {
  objects: "We could not load this list from Salesforce. Try again.",
  fields: "We could not load the Salesforce fields. Try again.",
  campaigns:
    "We could not load your campaigns from Salesforce. The Salesforce user who connected Encharge may need the Marketing User permission.",
  statuses: "We could not load this list from Salesforce. Try again.",
  owners: "We could not load this list from Salesforce. Try again.",
};

/**
 * The words the server's own refusals for a gate use (stepGate.ts). They are
 * written for the engine's log and name the sync's version, which a customer is
 * never shown, so they are replaced by the screens' own plain sentences.
 */
export const NOT_V2_SERVER_TEXT =
  "Salesforce steps need the new Salesforce sync (v2), which is not turned on for this account.";
export const NOT_IN_PLAN_SERVER_TEXT =
  "Salesforce steps are not part of this account's plan.";
export const ENGINE_ONLY_SERVER_TEXT =
  "These routes are for Encharge's own flow engine.";

/** The lead-in the flow engine puts in front of a step route's sentence. */
const ENGINE_LEAD_IN = /^Error while contacting [^:]*:\s*/;

export type StepListErrorText = {
  text: string;
  /** The Salesforce login needs to be renewed: the screen offers the way to do it */
  reconnect?: boolean;
};

/**
 * What a failed list says. A refusal (4xx) comes with a sentence the step
 * routes wrote for the person, which is said as it is, after the engine's
 * lead-in is taken off; the gate's technical refusals get plain sentences of
 * their own. A fault (5xx) or no answer (the network) has only a technical
 * message, which a customer must not be shown.
 */
export const stepListErrorText = (
  error: StepListError | null | undefined,
  kind: StepListKind
): StepListErrorText => {
  if (!error || error.status === undefined) {
    // sfGeneric.error.network
    return {
      text: "We could not reach Encharge. Check your connection and try again.",
    };
  }
  const said = (error.serverText || "").replace(ENGINE_LEAD_IN, "").trim();
  if (said === NOT_V2_SERVER_TEXT) {
    // sfGeneric.error.notV2
    return {
      text: "The new Salesforce sync is not switched on for your account yet.",
    };
  }
  if (said === NOT_IN_PLAN_SERVER_TEXT) {
    // sfGeneric.error.plan
    return { text: "This feature is not available on your plan." };
  }
  if (said === ENGINE_ONLY_SERVER_TEXT) {
    // sfGeneric.error.permission
    return {
      text: "You do not have permission to change Salesforce settings.",
    };
  }
  if (error.status >= 400 && error.status < 500 && said !== "") {
    return {
      text: said,
      reconnect: /reconnect salesforce/i.test(said),
    };
  }
  if (error.status === 429) {
    return { text: "The Salesforce API limit was reached. Try again later." };
  }
  return { text: FALLBACK_TEXT[kind] };
};

/**
 * Whether asking again could help. A refusal the person has to put right first
 * (the account is not on the new sync, the plan has no Salesforce, no
 * permission) will not change by itself.
 */
export const stepListCanTryAgain = (
  error: StepListError | null | undefined
): boolean => {
  if (!error) return false;
  const said = (error.serverText || "").replace(ENGINE_LEAD_IN, "").trim();
  return (
    said !== NOT_V2_SERVER_TEXT &&
    said !== NOT_IN_PLAN_SERVER_TEXT &&
    said !== ENGINE_ONLY_SERVER_TEXT
  );
};

// ---------------------------------------------------------------------------
// Update Salesforce Field

export type RecordKind = "Lead" | "Contact" | "Account";
export const RECORD_KINDS: readonly RecordKind[] = [
  "Lead",
  "Contact",
  "Account",
];
export const isRecordKind = (value: unknown): value is RecordKind =>
  (RECORD_KINDS as readonly unknown[]).includes(value);

/**
 * What the marketer reads for each record the step can update. The server's own
 * titles ("Lead", "Contact", "Account (the person's main Account)") are for a
 * list with no other words around it; here the question is "Update", and
 * flow.update.record.person / .account say what is meant.
 */
export const RECORD_OPTIONS: ReadonlyArray<{
  value: RecordKind;
  label: string;
}> = [
  { value: "Lead", label: "The person's Lead" },
  { value: "Contact", label: "The person's Contact" },
  { value: "Account", label: "The person's main Account" },
];

export const recordOptionLabel = (kind: RecordKind, serverTitle?: string) =>
  RECORD_OPTIONS.find((option) => option.value === kind)?.label ||
  serverTitle ||
  kind;

export type UpdateFieldRow = {
  /** The Salesforce field's API name; empty for a row that has no field yet */
  field: string;
  /** Text, or text with merge tags in it */
  value: string;
};

export type UpdateFieldSettings = {
  objectType: RecordKind | undefined;
  rows: UpdateFieldRow[];
  allowBlank: boolean;
};

const EMPTY_ROW: UpdateFieldRow = { field: "", value: "" };

/** The Update field step's settings from a step's stored values (a row to fill in when it has none). */
export const readUpdateFieldSettings = (
  values: unknown
): UpdateFieldSettings => {
  const v = isRecord(values) ? values : {};
  const rows: UpdateFieldRow[] = isRecord(v.fields)
    ? Object.entries(v.fields).map(([field, value]) => ({
        field,
        value: asText(value),
      }))
    : [];
  return {
    objectType: isRecordKind(v.objectType) ? v.objectType : undefined,
    rows: rows.length > 0 ? rows : [{ ...EMPTY_ROW }],
    allowBlank: v.allowBlank === true || v.allowBlank === "true",
  };
};

const isBlank = (text: string) => text.trim() === "";

/** The rows that name a field. A field named twice keeps its last value. */
const namedRows = (rows: UpdateFieldRow[]): UpdateFieldRow[] => {
  const byField = new Map<string, UpdateFieldRow>();
  rows.forEach((row) => {
    if (row.field !== "") byField.set(row.field, row);
  });
  return Array.from(byField.values());
};

/** What is saved for the Update field step: { objectType, fields, allowBlank }. */
export const updateFieldFormValues = (settings: UpdateFieldSettings) => {
  const fields: Record<string, string> = {};
  namedRows(settings.rows).forEach((row) => {
    fields[row.field] = row.value;
  });
  return {
    objectType: settings.objectType,
    fields,
    allowBlank: settings.allowBlank,
  };
};

/** One field of the chosen record, as the server's list of fields describes it. */
export type FieldInfo = {
  name: string;
  label: string;
  kind: "text" | "number" | "date" | "datetime" | "boolean" | "choice";
  choices: SelectOption[];
  /** The server's words about the field (for example how to give several values) */
  help?: string;
};

/**
 * The fields of the schema the list of fields answers with (updateFieldsPicker):
 * one property for each field the step may write, with its Salesforce label as
 * the title, its type, and the choices of a list field as `enum` and
 * `enumNames`.
 */
export const fieldsFromSchema = (schema: unknown): FieldInfo[] => {
  const properties =
    isRecord(schema) && isRecord(schema.properties) ? schema.properties : {};
  return Object.entries(properties).flatMap(([name, property]) => {
    if (!isRecord(property)) return [];
    const choices: SelectOption[] = Array.isArray(property.enum)
      ? property.enum.map((value, index) => {
          const names = Array.isArray(property.enumNames)
            ? property.enumNames
            : [];
          return {
            value: asText(value),
            label: asText(names[index]) || asText(value),
          };
        })
      : [];
    let kind: FieldInfo["kind"] = "text";
    if (choices.length > 0) kind = "choice";
    else if (property.type === "boolean") kind = "boolean";
    else if (property.type === "integer" || property.type === "number") {
      kind = "number";
    } else if (property.format === "date") kind = "date";
    else if (property.format === "date-time") kind = "datetime";
    return [
      {
        name,
        label:
          typeof property.title === "string" && property.title !== ""
            ? property.title
            : name,
        kind,
        choices,
        ...(typeof property.description === "string" &&
        property.description !== ""
          ? { help: property.description }
          : {}),
      },
    ];
  });
};

/**
 * What the picker of fields shows for each field. Two fields can have the same
 * Salesforce label, so a label that is not unique carries the API name too.
 */
export const fieldOptions = (fields: FieldInfo[]): SelectOption[] => {
  const seen = new Map<string, number>();
  fields.forEach((field) =>
    seen.set(field.label, (seen.get(field.label) || 0) + 1)
  );
  return fields.map((field) => ({
    value: field.name,
    label:
      (seen.get(field.label) || 0) > 1
        ? `${field.label} (${field.name})`
        : field.label,
  }));
};

/** The labels of the fields by API name, for the sentences. */
export const labelsOf = (fields: FieldInfo[]): Record<string, string> => {
  const labels: Record<string, string> = {};
  fields.forEach((field) => {
    labels[field.name] = field.label;
  });
  return labels;
};

/**
 * The labels a step already holds: its stored list of fields has the Salesforce
 * label as the title of each property. A card works from these, so it needs no
 * call to Salesforce.
 */
export const storedFieldLabels = (
  inputFields: unknown
): Record<string, string> => {
  const labels: Record<string, string> = {};
  const properties =
    isRecord(inputFields) &&
    isRecord(inputFields.properties) &&
    isRecord(inputFields.properties.fields) &&
    isRecord(inputFields.properties.fields.properties)
      ? inputFields.properties.fields.properties
      : {};
  Object.entries(properties).forEach(([name, property]) => {
    if (
      isRecord(property) &&
      typeof property.title === "string" &&
      property.title !== ""
    ) {
      labels[name] = property.title;
    }
  });
  return labels;
};

/**
 * The step's list of fields with the fields of the chosen record put in. The
 * step stores the values of its fields inside that list (each field is a
 * property of it), and the server hands the list out empty until a record is
 * chosen, so the list the screen read is what the values are put into. The
 * mark that says the list is expanded is kept, as the editor's own reload of a
 * dynamic list keeps it.
 */
export const withFieldsSchema = (
  inputFields: any,
  schema: Record<string, any> | undefined
): any => {
  if (!schema) return inputFields;
  const properties = isRecord(inputFields?.properties)
    ? inputFields.properties
    : {};
  const mark = properties.fields?.["x-encharge-dynamic"];
  const directive = isRecord(mark)
    ? { ...mark, expanded: true }
    : {
        operation:
          typeof mark === "string" ? mark : STEP_LIST_OPERATIONS.fields,
        expanded: true,
      };
  return {
    ...inputFields,
    properties: {
      ...properties,
      fields: { ...schema, "x-encharge-dynamic": directive },
    },
  };
};

export type UpdateFieldProblem = {
  objectType?: string;
  /** A problem with the rows as a whole */
  rows?: string;
  /** A problem with one row, by its place in the list */
  row?: Record<number, { field?: string }>;
};

/**
 * What stops the Update field step from being saved, or undefined. `known` is
 * the list of fields that was read for the chosen record: undefined while it is
 * not read, and then a field cannot be told to be gone.
 */
export const updateFieldProblem = (
  settings: UpdateFieldSettings,
  known: FieldInfo[] | undefined
): UpdateFieldProblem | undefined => {
  const problem: UpdateFieldProblem = {};
  if (settings.objectType === undefined) {
    problem.objectType = "Choose what to update.";
    return problem;
  }
  const named = settings.rows.filter((row) => row.field !== "");
  settings.rows.forEach((row, index) => {
    if (row.field === "" && !isBlank(row.value)) {
      problem.row = { ...problem.row, [index]: { field: "Pick a field." } };
    } else if (
      row.field !== "" &&
      known &&
      known.length > 0 &&
      !known.some((field) => field.name === row.field)
    ) {
      problem.row = {
        ...problem.row,
        [index]: {
          field:
            "This field cannot be written in your Salesforce any more. Pick another field.",
        },
      };
    }
  });
  if (named.length === 0) {
    problem.rows = "Pick at least one field to update.";
  } else if (!settings.allowBlank && named.every((row) => isBlank(row.value))) {
    problem.rows =
      "Give at least one field a value, or choose to clear a field when its value is empty.";
  }
  return problem.objectType || problem.rows || problem.row
    ? problem
    : undefined;
};

const valueText = (value: string, max = 60): string =>
  value.trim() === "" ? "empty" : shortened(value.trim(), max);

const fieldLabel = (labels: Record<string, string>, name: string) =>
  labels[name] || name;

/** The fields and values the step will write, as "A to X, B to Y and C to Z". */
const writtenList = (
  settings: UpdateFieldSettings,
  labels: Record<string, string>
): string[] =>
  namedRows(settings.rows)
    // an empty value is written only when the step is set to clear
    .filter((row) => settings.allowBlank || !isBlank(row.value))
    .map(
      (row) => `${fieldLabel(labels, row.field)} to ${valueText(row.value)}`
    );

/**
 * "What this step will do" for the Update field step: flow.update.preview.person
 * and flow.update.preview.account. Undefined until a record and a field are
 * chosen.
 */
export const previewUpdateField = (
  settings: UpdateFieldSettings,
  labels: Record<string, string>
): string | undefined => {
  if (settings.objectType === undefined) return undefined;
  const written = writtenList(settings, labels);
  if (written.length === 0) return undefined;
  const where =
    settings.objectType === "Account"
      ? "the person's main Account"
      : `the person's ${settings.objectType}`;
  return `Will set ${andList(written)} on ${where}.`;
};

/**
 * The sentence on the step's card: flow.update.canvas, "Set {field} to {value}
 * in Salesforce". Undefined until a field is chosen (the card then shows what
 * the server wrote for the step).
 */
export const describeUpdateField = (
  settings: UpdateFieldSettings,
  labels: Record<string, string>
): string | undefined => {
  const named = namedRows(settings.rows);
  if (settings.objectType === undefined || named.length === 0) return undefined;
  if (named.length === 1) {
    // a card is narrow: a shorter cut than the sentence in the form
    return `Set ${fieldLabel(labels, named[0].field)} to ${valueText(
      named[0].value,
      40
    )} in Salesforce`;
  }
  return `Set ${named.length} fields in Salesforce: ${andList(
    named.map((row) => fieldLabel(labels, row.field))
  )}`;
};

// ---------------------------------------------------------------------------
// Create Task in Salesforce

export const TASK_PRIORITIES = ["High", "Normal", "Low"] as const;
export type TaskPriority = typeof TASK_PRIORITIES[number];
export type TaskPer = "opportunity" | "person";

/** The Task's `ownerId` setting that means "whoever owns the person's record" (RECORD_OWNER on the server). */
export const RECORD_OWNER = "recordOwner";

/** The most days ahead the step accepts (createTaskStep.ts, parseCreateTaskInput). */
export const TASK_DUE_MAX_DAYS = 3650;

export type CreateTaskSettings = {
  subject: string;
  description: string;
  /** As typed; empty means no due date */
  dueInDays: string;
  priority: TaskPriority;
  /** RECORD_OWNER, the id of a Salesforce user, or empty while "a specific user" has no user yet */
  ownerId: string;
  taskPer: TaskPer;
};

export const isTaskPriority = (value: unknown): value is TaskPriority =>
  (TASK_PRIORITIES as readonly unknown[]).includes(value);

/** The Create Task step's settings from a step's stored values, with the definition's defaults. */
export const readCreateTaskSettings = (values: unknown): CreateTaskSettings => {
  const v = isRecord(values) ? values : {};
  return {
    subject: asText(v.subject),
    description: asText(v.description),
    dueInDays: asText(v.dueInDays),
    priority: isTaskPriority(v.priority) ? v.priority : "Normal",
    ownerId:
      typeof v.ownerId === "string" && v.ownerId !== ""
        ? v.ownerId
        : RECORD_OWNER,
    taskPer: v.taskPer === "person" ? "person" : "opportunity",
  };
};

/** The number of days when the text is a whole number from 0 up to the most the step accepts. */
export const dueDays = (text: string): number | undefined => {
  const trimmed = text.trim();
  if (!/^\d{1,4}$/.test(trimmed)) return undefined;
  const days = Number(trimmed);
  return days <= TASK_DUE_MAX_DAYS ? days : undefined;
};

/** What is saved for the Create Task step. No notes and no due date are saved as nothing. */
export const createTaskFormValues = (settings: CreateTaskSettings) => ({
  subject: settings.subject,
  description: isBlank(settings.description) ? undefined : settings.description,
  dueInDays: isBlank(settings.dueInDays)
    ? undefined
    : dueDays(settings.dueInDays),
  priority: settings.priority,
  ownerId: settings.ownerId,
  taskPer: settings.taskPer,
});

export type CreateTaskProblem = {
  subject?: string;
  dueInDays?: string;
  ownerId?: string;
};

/**
 * What stops the Create Task step from being saved, or undefined. `owners` is
 * the list of users that was read: undefined while it is not read, and then an
 * owner cannot be told to be gone.
 */
export const createTaskProblem = (
  settings: CreateTaskSettings,
  owners: StepPick[] | undefined
): CreateTaskProblem | undefined => {
  const problem: CreateTaskProblem = {};
  // createTaskStep.ts: create_task_no_subject
  if (isBlank(settings.subject)) problem.subject = "Give the Task a subject.";
  // createTaskStep.ts: create_task_bad_due
  if (
    !isBlank(settings.dueInDays) &&
    dueDays(settings.dueInDays) === undefined
  ) {
    problem.dueInDays = `Due in (days) must be a whole number from 0 to ${TASK_DUE_MAX_DAYS}.`;
  }
  if (settings.ownerId === "") {
    // "A specific Salesforce user" was chosen and nobody is picked yet
    problem.ownerId = "Pick a Salesforce user.";
  } else if (
    settings.ownerId !== RECORD_OWNER &&
    listIsComplete(owners) &&
    !owners!.some((owner) => owner.id === settings.ownerId)
  ) {
    // flow.task.error.owner
    problem.ownerId =
      "That owner is not an active Salesforce user. Pick another owner.";
  }
  return problem.subject || problem.dueInDays || problem.ownerId
    ? problem
    : undefined;
};

const daysText = (days: number) => (days === 1 ? "1 day" : `${days} days`);

/** The name an owner is shown by: the user's name, or the words for "the record's owner". */
export const ownerWords = (
  ownerId: string,
  owners: StepPick[] | undefined
): string => {
  if (ownerId === RECORD_OWNER) return "the owner of the person's record";
  return (
    owners?.find((owner) => owner.id === ownerId)?.title ||
    "the Salesforce user you chose"
  );
};

/**
 * "What this step will do" for Create Task: flow.task.preview and
 * flow.task.preview.noDue, with a line on whether it is one Task for an Opportunity
 * when the flow starts from one. Undefined until the Task has a subject.
 */
export const previewCreateTask = (
  settings: CreateTaskSettings,
  owners: StepPick[] | undefined,
  startsFromOpportunity: boolean
): string | undefined => {
  if (isBlank(settings.subject)) return undefined;
  const subject = shortened(settings.subject.trim(), 60);
  const days = isBlank(settings.dueInDays)
    ? undefined
    : dueDays(settings.dueInDays);
  const owner = ownerWords(settings.ownerId, owners);
  const head =
    days === undefined
      ? `Will create the Task '${subject}' for ${owner}.`
      : `Will create the Task '${subject}', due in ${daysText(
          days
        )}, for ${owner}.`;
  if (!startsFromOpportunity) return head;
  return settings.taskPer === "opportunity"
    ? `${head} One Task for the Opportunity, however many people are on it.`
    : `${head} One Task for each person.`;
};

/** The sentence on the step's card: flow.task.canvas. Undefined until the Task has a subject. */
export const describeCreateTask = (
  settings: CreateTaskSettings
): string | undefined =>
  isBlank(settings.subject)
    ? undefined
    : `Create Salesforce Task: ${shortened(settings.subject.trim(), 60)}`;

// ---------------------------------------------------------------------------
// Add to and Remove from Campaign

export type CampaignStepSettings = {
  /** The Campaign's Salesforce id; empty when not chosen */
  campaignId: string;
  /** The member status by its name; empty means the Campaign's own default (Add only) */
  status: string;
};

export const readCampaignStepSettings = (
  values: unknown
): CampaignStepSettings => {
  const v = isRecord(values) ? values : {};
  return {
    campaignId: typeof v.campaignId === "string" ? v.campaignId : "",
    status: typeof v.status === "string" ? v.status : "",
  };
};

/** What is saved for Add to Campaign. A status that is not chosen is saved as nothing. */
export const addToCampaignFormValues = (settings: CampaignStepSettings) => ({
  campaignId: settings.campaignId,
  status: settings.status === "" ? undefined : settings.status,
});

/** What is saved for Remove from Campaign: the Campaign only. */
export const removeFromCampaignFormValues = (
  settings: CampaignStepSettings
) => ({ campaignId: settings.campaignId });

export type CampaignStepProblem = { campaignId?: string; status?: string };

/**
 * What stops a Campaign step from being saved, or undefined. `campaigns` and
 * `statuses` are the lists that were read (undefined while not read); a Campaign
 * or a status is called gone only against a list that was really read.
 */
export const campaignStepProblem = (
  settings: CampaignStepSettings,
  lists: {
    campaigns: StepPick[] | undefined;
    statuses: StepPick[] | undefined;
    withStatus: boolean;
  }
): CampaignStepProblem | undefined => {
  const problem: CampaignStepProblem = {};
  if (settings.campaignId === "") {
    // campaignSteps.ts: campaign_step_no_campaign
    problem.campaignId = "Choose a Salesforce Campaign.";
  } else if (
    listIsComplete(lists.campaigns) &&
    !lists.campaigns!.some((pick) => pick.id === settings.campaignId)
  ) {
    // flow.cadd.error.gone
    problem.campaignId = "This campaign no longer exists in Salesforce.";
  } else if (
    lists.withStatus &&
    settings.status !== "" &&
    lists.statuses &&
    lists.statuses.length > 0 &&
    !lists.statuses.some((pick) => pick.id === settings.status)
  ) {
    // flow.cadd.error.status
    problem.status = `'${settings.status}' is not a member status of this campaign. Pick one of its statuses.`;
  }
  return problem.campaignId || problem.status ? problem : undefined;
};

/** "What this step will do" for Add to Campaign: flow.cadd.preview. */
export const previewAddToCampaign = (
  settings: CampaignStepSettings,
  campaign: string | undefined
): string | undefined => {
  if (settings.campaignId === "" || !campaign) return undefined;
  return settings.status === ""
    ? `Will add the person to the Salesforce campaign '${campaign}' with the campaign's default status.`
    : `Will add the person to the Salesforce campaign '${campaign}' with the status '${settings.status}'.`;
};

/** "What this step will do" for Remove from Campaign: flow.crem.preview. */
export const previewRemoveFromCampaign = (
  settings: CampaignStepSettings,
  campaign: string | undefined
): string | undefined =>
  settings.campaignId === "" || !campaign
    ? undefined
    : `Will remove the person from the Salesforce campaign '${campaign}'.`;

/**
 * The sentences on the cards: flow.cadd.canvas and flow.crem.canvas. The
 * Campaign is stored by its id only, so the name comes from the list of
 * Campaigns; until it is known the card says "a Salesforce campaign". Undefined
 * until a Campaign is chosen.
 */
export const describeAddToCampaign = (
  settings: CampaignStepSettings,
  campaign: string | undefined
): string | undefined =>
  settings.campaignId === ""
    ? undefined
    : campaign
    ? `Add to Salesforce campaign: ${shortened(campaign, 60)}`
    : "Add to a Salesforce campaign";

export const describeRemoveFromCampaign = (
  settings: CampaignStepSettings,
  campaign: string | undefined
): string | undefined =>
  settings.campaignId === ""
    ? undefined
    : campaign
    ? `Remove from Salesforce campaign: ${shortened(campaign, 60)}`
    : "Remove from a Salesforce campaign";

// ---------------------------------------------------------------------------
// Words shared by the four steps (flow.step.*)

/** flow.step.writesAs */
export const NOTE_WRITES_AS =
  "This step writes to Salesforce as the user who connected Encharge.";
/** flow.step.noRecord.note */
export const NOTE_NO_RECORD =
  "If a person has no Salesforce record, they continue to the next step and nothing is written. Encharge does not create Salesforce records here. To create one first, use the Create Lead/Contact step.";
/**
 * flow.step.apiHold. What the code does (api steps/apiHold.ts, engine job.ts):
 * above 80% of the day's allowance a person waits and the step asks again
 * after an hour (and up to ten minutes more, picked at random); a person
 * still held after a week fails at the step. The tab those people are listed
 * on is said by the note that follows in every form (NOTE_FAILED_TAB), so it is
 * not said twice.
 */
export const NOTE_API_HOLD =
  "This step waits while your Salesforce org is above 80% of its daily API allowance. We check again about every hour. Someone still waiting after 7 days fails at this step.";
/** flow.step.failedTab */
export const NOTE_FAILED_TAB =
  'People this step fails for are listed on the step\'s "Step failed for people" tab.';
